import { Bulb, CircleInfo } from "@gravity-ui/icons";
import { Flex, Icon, Spin, Text } from "@gravity-ui/uikit";

import { PreviewFieldResult } from "./PreviewFieldResult";
import css from "./TemplatePreviewPanel.module.scss";
import type { PreviewSample } from "./TemplatesForm.constants";
import type {
  TemplateApiError,
  TemplatePreviewResponse,
  TemplatePreviewSample,
} from "./TemplatesForm.types";

// Стабильный пустой массив, чтобы samples не получал новую ссылку на каждый рендер.
const EMPTY_SAMPLES: TemplatePreviewSample[] = [];

interface TemplatePreviewPanelProps {
  field: "title" | "description";
  data: TemplatePreviewResponse | null;
  isLoading: boolean;
  error: TemplateApiError | null;
  products: PreviewSample[];
  hasTemplate: boolean;
  warning?: string;
}

export const TemplatePreviewPanel = ({
  field,
  data,
  isLoading,
  error,
  products,
  hasTemplate,
  warning,
}: TemplatePreviewPanelProps) => {
  const samples = data?.samples ?? EMPTY_SAMPLES;

  if (!hasTemplate) {
    return (
      <div className={`${css.panel} ${css.panelEmpty}`}>
        <Icon data={Bulb} size={20} />
        <Text variant="body-2" color="secondary">
          Введите шаблон — здесь появится превью на 3 разных товарах.
        </Text>
      </div>
    );
  }

  // Generic-ошибки (network, 500, class-validator) выводим единым алертом.
  // Parse/unknown ошибки уже отрисованы под редактором — здесь не дублируем.
  const showGenericError =
    error && error.kind !== "parse" && error.kind !== "unknown";

  return (
    <div className={css.panel}>
      <div className={css.header}>
        <Flex alignItems="center" gap={2}>
          <Text variant="subheader-2">Превью на сэмплах</Text>
          {isLoading && <Spin size="xs" />}
        </Flex>
        <Text variant="caption-2" color="hint">
          Шаблон применяется ко всем подходящим товарам в момент экспорта.
        </Text>
      </div>

      {warning && (
        <div className={css.warning} role="status">
          <Icon data={CircleInfo} size={16} />
          <Text variant="body-2">{warning}</Text>
        </div>
      )}

      {showGenericError && (
        <div className={css.error} role="alert">
          <Text variant="body-2">{error.message}</Text>
        </div>
      )}

      {samples.length === 0 && !isLoading && !error && (
        <Text variant="body-2" color="hint">
          Нет данных для превью.
        </Text>
      )}

      <div className={css.samples}>
        {samples.map((sample) => {
          const product = products.find(
            (p) => p.productId === sample.productId,
          );
          const label =
            product?.displayLabel ??
            product?.title ??
            `Товар #${sample.productId}`;
          const result = field === "title" ? sample.title : sample.description;
          return (
            <article key={sample.productId} className={css.sample}>
              <header className={css.sampleHeader}>
                <Text variant="subheader-1">{label}</Text>
                <Text variant="caption-2" color="hint">
                  ID {sample.productId}
                </Text>
              </header>
              <PreviewFieldResult result={result} />
            </article>
          );
        })}
      </div>
    </div>
  );
};
